import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import toast from "react-hot-toast";

import CategoryServices from "../../../services/OrderingServices/CategoryService";
import DeleteModal from "../../../Components/Common/DeleteModal";
import MobileCategoryForm from "../component/MobileCategoryForm";
import useIsSmallScreen from "../../Payment Type/Component/small screen/SmallScreen";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";

// ✅ STEP 1: Import the reusable hook
import useServerSideSearch from "../../../hooks/useServerSideSearch";
import StatusBadge from "../../../Components/Common/Badge";

const CategoryList = () => {
  const navigate = useNavigate();
  const isSmallScreen = useIsSmallScreen();
  // ✅ STEP 2: Use the hook with your API
  const {
    data: categoryList,
    loading,
    totalRows,
    stats: apiStats,
    currentPage,
    pageSize,
    searchQuery,
    handleSearch,
    handlePaginationChange,
    refetch,
  } = useServerSideSearch(CategoryServices.maincategory);

  // Modal states
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // ✅ STEP 3: Define your columns
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      sortable: false,
      width: "75px",
    },
    {
      name: "Category",
      selector: (row) => row.name,
      cell: (row) => <Link to={`/category-detail/${row.id}`}>{row.name}</Link>,
      sortable: true,
      wrap: true,
    },
    {
      name: "Parent Category",
      selector: (row) => row.parentName || "None",
      sortable: true,
      width: "25%",
      minWidth: "150px",
      wrap: true,
    },
    {
      name: "Cost Center",
      selector: (row) => row.costCenterName || "None",
      sortable: true,
      wrap: true,
    },
    {
      name: "Status",
      selector: (row) => row.isActive,
      cell: (row) => (
        <StatusBadge status={row.isActive} type="status" pill={true} />
      ),
      sortable: true,
    },

    {
      name: "Hide Status",
      selector: (row) => row.isHidden,
      cell: (row) => (
        <StatusBadge status={row.isHidden} type="status" pill={true} />
      ),
      sortable: true,
    },
  ];

  // ✅ STEP 4: Define your stats
  const stats = [
    {
      title: "Total Categories",
      value: apiStats?.totalCategories || 0,
      color: "#0d6efd",
      icon: "bx bx-category",
    },
    {
      title: "Active Categories",
      value: apiStats?.notHiddenCategories || 0,
      color: "#28a745",
      icon: "bx bx-check-circle",
    },
    {
      title: "Hidden Categories",
      value: apiStats?.hiddenCategories || 0,
      color: "#ffc107",
      icon: "bx bx-hide",
    },
    {
      title: "Parent Category",
      value: apiStats?.parentCategoryCount || 0,
      color: "#17a2b8",
      icon: "bx bx-layer",
    },
  ];

  // ✅ STEP 5: Handle Delete
  const handleDelete = async (category) => {
    try {
      await CategoryServices.deletecategory(category.id);
      toast.success("Category deleted successfully");
      refetch(); // ← Use refetch to reload data
      setIsOpenDelete(false);
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error(
        error?.response?.data?.message || "Failed to delete category"
      );
    }
  };

  // Handle modal for add/edit
  const handleAdd = () => {
    navigate("/category/create");
  };

  const handleEdit = (category) => {
    navigate(`/category/update/${category.id}`);
  };

  const handleView = (category) => {
    navigate(`/category-detail/${category.id}`);
  };

  const toggleDelete = () => {
    setIsOpenDelete(!isOpenDelete);
  };

  const toggleModal = (item) => {
    setSelectedDeleteItem(item);
    setIsOpen(!isOpen);
  };

  // ✅ STEP 6: Render
  return (
    <>
      <ResponsiveDataManagement
        title="Category"
        pageTitle="Inventory Management"
        mobileCardTitle="Category List"
        data={categoryList}
        columns={columns}
        stats={stats}
        searchKeys={[
          "name",
          "parentName",
          "costCenterName",
          "isActive",
          "isHidden",
        ]}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={loading}
        emptyStateMessage="No Category found"
        totalRows={totalRows}
        currentPage={currentPage}
        pageSize={pageSize}
        onSearch={handleSearch} // ← Pass search handler
        searchTerm={searchQuery} // ← Pass search term
        onPaginationChange={handlePaginationChange}
        mobileCardRenderer={(item) => (
          <Card
            className="shadow-sm rounded-3 border-0 border-start border-4 border-primary mb-3"
            onClick={() => handleView(item)}
          >
            <CardBody>
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="fw-bold text-primary mb-2">{item.name}</h6>
                  <p className="mb-1 text-muted small">
                    <strong>Parent Category:</strong>{" "}
                    {item.parentName || "None"}
                  </p>
                  <p className="mb-1 text-muted small">
                    <strong>Cost Center:</strong>{" "}
                    {item.costCenterName || "None"}
                  </p>
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    <span
                      className={`badge ${
                        item.isActive ? "bg-success" : "bg-danger"
                      }`}
                    >
                      {item.isActive ? "Active" : "Inactive"}
                    </span>
                    <span
                      className={`badge ${
                        item.isHidden ? "bg-success" : "bg-danger"
                      }`}
                    >
                      {item.isHidden ? "Visible" : "Hidden"}
                    </span>
                  </div>
                </div>

                <Dropdown
                  isOpen={openDropdownId === item.id}
                  toggle={() =>
                    setOpenDropdownId(
                      openDropdownId === item.id ? null : item.id
                    )
                  }
                  direction="down"
                >
                  <DropdownToggle
                    color="light"
                    className="btn-sm rounded-circle shadow-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-more-2-fill" />
                  </DropdownToggle>

                  <DropdownMenu end>
                    <DropdownItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(item);
                        setOpenDropdownId(null);
                      }}
                    >
                      <i className="ri-edit-line me-2" />
                      Edit
                    </DropdownItem>

                    <DropdownItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDeleteItem(item);
                        setIsOpenDelete(true);
                        setOpenDropdownId(null);
                      }}
                    >
                      <i className="ri-delete-bin-line me-2" />
                      Delete
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </CardBody>
          </Card>
        )}
      />

      <DeleteModal
        show={isOpenDelete}
        onCloseClick={toggleDelete}
        onDeleteClick={() => handleDelete(selectedDeleteItem)}
      />
      <MobileCategoryForm
        isOpen={isOpen}
        toggle={toggleModal}
        selectedItem={selectedDeleteItem}
        fetchCategories={refetch} // ← Pass refetch instead
      />
    </>
  );
};

export default CategoryList;
